import { recoverTransactionAddress, parseTransaction } from 'viem'

import { assertValidSignatureInput } from '../common/validation'

import type {
  EIP1559SigningPayload,
  EIP1559Transaction,
  SignatureInput,
  ValidationResult,
} from '../../types'
import type { TransactionEIP1559 } from 'viem'

/**
 * Validates an EIP-1559 signed transaction against an expected payload.
 */
export async function validateEIP1559Signature(
  payload: EIP1559SigningPayload,
  signature: SignatureInput,
): Promise<ValidationResult> {
  try {
    assertValidSignatureInput(signature.signature)
    if (
      !(
        typeof signature.signature === 'string' &&
        /^0x[0-9a-fA-F]+$/.test(signature.signature)
      )
    ) {
      return {
        success: false,
        message: 'Invalid signed transaction format',
        details: 'Signed transaction must be a hex string starting with 0x',
      }
    }

    let parsedTransaction
    try {
      parsedTransaction = parseTransaction(
        signature.signature as `0x02${string}`,
      )
    } catch (error) {
      return {
        success: false,
        message: 'Failed to parse signed transaction',
        details:
          error instanceof Error ? error.message : 'Unknown parsing error',
      }
    }

    if (parsedTransaction.type !== 'eip1559') {
      return {
        success: false,
        message: 'Transaction is not EIP-1559 type',
        details: `Expected type 'eip1559', got '${String(parsedTransaction.type)}'`,
      }
    }

    const eip1559Transaction = parsedTransaction as TransactionEIP1559

    let recoveredAddress: string
    try {
      recoveredAddress = await recoverTransactionAddress({
        serializedTransaction: signature.signature as `0x02${string}`,
      })
    } catch (error) {
      return {
        success: false,
        message: 'Could not recover address from signed transaction',
        details: error instanceof Error ? error.message : 'Recovery failed',
      }
    }

    if (
      recoveredAddress.toLowerCase() !== payload.walletAddress.toLowerCase()
    ) {
      return {
        success: false,
        message: 'Address mismatch - transaction verification failed',
        details: `Recovered: ${recoveredAddress}, Expected: ${payload.walletAddress}`,
      }
    }

    const expectedPayload = payload.payload
    const contentValidation = validateTransactionContent(
      eip1559Transaction as Record<string, unknown>,
      expectedPayload,
    )
    if (!contentValidation.isValid) {
      return {
        success: false,
        message: 'Transaction content validation failed',
        details: contentValidation.details,
      }
    }

    return {
      success: true,
      message: 'EIP-1559 signed transaction verified successfully',
      details: `Address ${recoveredAddress} successfully recovered from signed transaction`,
    }
  } catch (error) {
    return {
      success: false,
      message: 'Transaction verification failed',
      details:
        error instanceof Error ? error.message : 'Unknown verification error',
    }
  }
}

function validateTransactionContent(
  parsedTx: Record<string, unknown>,
  expectedPayload: EIP1559Transaction,
): { isValid: boolean; details?: string } {
  type NumericType = string | number | bigint

  // to
  const parsedTo = parsedTx.to as string | undefined
  const expectedTo = expectedPayload.to
  if (parsedTo?.toLowerCase() !== expectedTo.toLowerCase()) {
    return {
      isValid: false,
      details: `To address mismatch: ${parsedTo || 'undefined'} vs ${expectedTo}`,
    }
  }

  // value
  const parsedValueRaw = parsedTx.value as NumericType | undefined
  const parsedValue = BigInt(parsedValueRaw ?? 0)
  const expectedValue = BigInt(expectedPayload.value)
  if (parsedValue !== expectedValue) {
    return {
      isValid: false,
      details: `Value mismatch: ${parsedValue.toString()} vs ${expectedValue.toString()}`,
    }
  }

  // data (input)
  const inputData = parsedTx.input as string | undefined
  const fallbackData = parsedTx.data as string | undefined
  const parsedData = inputData ?? fallbackData ?? '0x'
  const expectedData = expectedPayload.data
  if (parsedData !== expectedData) {
    return {
      isValid: false,
      details: `Data mismatch: ${parsedData} vs ${expectedData}`,
    }
  }

  // chainId
  const parsedChainIdRaw = parsedTx.chainId as NumericType | undefined
  const parsedChainId = BigInt(parsedChainIdRaw ?? 0)
  const expectedChainId = BigInt(expectedPayload.chainId)
  if (parsedChainId !== expectedChainId) {
    return {
      isValid: false,
      details: `Chain ID mismatch: ${parsedChainId.toString()} vs ${expectedChainId.toString()}`,
    }
  }

  // gas limit
  const parsedGasRaw = parsedTx.gas as NumericType | undefined
  const parsedGas = BigInt(parsedGasRaw ?? 0)
  const expectedGas = BigInt(expectedPayload.gasLimit)
  if (parsedGas !== expectedGas) {
    return {
      isValid: false,
      details: `Gas limit mismatch: ${parsedGas.toString()} vs ${expectedGas.toString()}`,
    }
  }

  // max fee per gas
  const parsedMaxFeeRaw = parsedTx.maxFeePerGas as NumericType | undefined
  const parsedMaxFee = BigInt(parsedMaxFeeRaw ?? 0)
  const expectedMaxFee = BigInt(expectedPayload.maxFeePerGas)
  if (parsedMaxFee !== expectedMaxFee) {
    return {
      isValid: false,
      details: `Max fee per gas mismatch: ${parsedMaxFee.toString()} vs ${expectedMaxFee.toString()}`,
    }
  }

  // max priority fee per gas
  const parsedPriorityFeeRaw = parsedTx.maxPriorityFeePerGas as
    | NumericType
    | undefined
  const parsedPriorityFee = BigInt(parsedPriorityFeeRaw ?? 0)
  const expectedPriorityFee = BigInt(expectedPayload.maxPriorityFeePerGas)
  if (parsedPriorityFee !== expectedPriorityFee) {
    return {
      isValid: false,
      details: `Max priority fee per gas mismatch: ${parsedPriorityFee.toString()} vs ${expectedPriorityFee.toString()}`,
    }
  }

  return { isValid: true }
}
